import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, ActivityIndicator, Text, ScrollView, TouchableOpacity, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal, FormHeader, FormHeaderAction } from '../components';
import { EventForm, AttendeeStatusBadge } from '../components/calendar';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { scheduleLocalReminders, cancelLocalReminders } from '../services/calendar-local-reminders.service';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface EventEditScreenProps {
  eventId?: string;
  calendarId?: string; // For create mode
}

export const EventEditScreen: React.FC<EventEditScreenProps> = ({ eventId: propEventId, calendarId: propCalendarId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [loading, setLoading] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(!!propEventId); // Only load if eventId exists
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [event, setEvent] = useState<any>(null);
  const [calendar, setCalendar] = useState<any>(null);
  const [calendars, setCalendars] = useState<any[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const [showLeftPane] = useState(Platform.OS === 'web' ? true : false);
  const [showRightPane] = useState(Platform.OS === 'web' ? true : false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const formRef = useRef<{ submit: () => void } | null>(null);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Get eventId and calendarId from props or routeParams
  const eventId = propEventId || routeParams?.eventId || '';
  const calendarId = propCalendarId || routeParams?.calendarId || event?.calendarId || '';
  const listId: string | undefined = routeParams?.listId;
  
  // Get selected date from route params for pre-filling
  const selectedDate = routeParams?.selectedDate ? new Date(routeParams.selectedDate) : null;

  const loadCalendar = useCallback(async (calendarId: string) => {
    try {
      const response = await apiService.getCalendarById(calendarId, currentBusiness?._id || '');
      if (response.statusCode === 1000 && response.data) {
        setCalendar(response.data);
        setSelectedCalendarId(calendarId);
      }
    } catch (error: any) {
      console.error('Error loading calendar:', error);
    }
  }, [currentBusiness]);

  const loadCalendars = useCallback(async () => {
    if (!currentBusiness?._id) return;
    
    try {
      setLoadingCalendars(true);
      const response = await apiService.getBusinessCalendars(currentBusiness._id);
      if (response.statusCode === 1000 && response.data) {
        const calendarsList = Array.isArray(response.data) ? response.data : [];
        setCalendars(calendarsList);
        
        // Find default calendar
        const defaultCalendar = calendarsList.find((cal: any) => cal.isDefault === true);
        if (defaultCalendar) {
          setCalendar(defaultCalendar);
          setSelectedCalendarId(defaultCalendar._id);
        } else if (calendarsList.length > 0) {
          // If no default, use first calendar
          setCalendar(calendarsList[0]);
          setSelectedCalendarId(calendarsList[0]._id);
        }
      }
    } catch (error: any) {
      console.error('Error loading calendars:', error);
    } finally {
      setLoadingCalendars(false);
    }
  }, [currentBusiness]);

  // Contact loading removed - using attendee data directly from event

  useEffect(() => {
    console.log('[EventEditScreen] eventId:', eventId, 'calendarId:', calendarId, 'currentBusiness?._id:', currentBusiness?._id);
    if (eventId && currentBusiness?._id) {
      // Edit mode: load event
      loadEvent();
    } else if (!eventId && calendarId && currentBusiness?._id) {
      // Create mode: load specific calendar
      setLoadingEvent(false);
      loadCalendar(calendarId);
      loadCalendars(); // Also load all calendars for selector
    } else if (!eventId && !calendarId && currentBusiness?._id) {
      // Create mode: no calendarId provided, load default calendar
      setLoadingEvent(false);
      loadCalendars();
    }
  }, [eventId, calendarId, currentBusiness?._id, loadCalendar, loadCalendars]);

  const loadEvent = async () => {
    if (!eventId || !currentBusiness?._id) {
      console.warn('[EventEditScreen] Cannot load event - missing eventId or businessId');
      setLoadingEvent(false);
      return;
    }

    try {
      console.log('[EventEditScreen] Loading event:', eventId);
      setLoadingEvent(true);
      const response = await apiService.getEventById(eventId, currentBusiness._id);
      console.log('[EventEditScreen] Event loaded:', response);

      if (response.statusCode === 1000 && response.data) {
        const eventData = response.data;
        // Convert date strings to Date objects
        const eventObj = {
          ...eventData,
          startDate: new Date(eventData.startDate),
          endDate: new Date(eventData.endDate),
        };
        setEvent(eventObj);

        // Load calendar
        if (eventData.calendarId) {
          loadCalendar(eventData.calendarId);
        }

        // Attendee data is already in event object, no need to load separately
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventEdit.loadFailed');
        showNotification('error', 'Error', errorMessage);
        navigateTo('calendars');
      }
    } catch (error: any) {
      console.error('Error loading event:', error);
      showNotification('error', 'Error', error.message || t('calendar.eventEdit.loadFailed'));
      navigateTo('calendars');
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleSubmit = async (formData: any) => {
    if (!currentBusiness?._id) {
      showNotification('error', 'Error', t('calendar.eventEdit.missingInfo'));
      return;
    }

    try {
      setLoading(true);
      
      if (eventId) {
        // Edit mode: update existing event
        const response = await apiService.updateEvent(eventId, formData);

        if (response.statusCode === 1000) {
          showNotification('success', 'Success', t('calendar.eventEdit.updateSuccess'));
          if (response.data && Array.isArray(formData.reminders)) {
            // Cancel-then-recreate, same as the backend's Task lifecycle —
            // fires even with an empty array to clear stale local reminders.
            scheduleLocalReminders(response.data, formData.reminders).catch((err) =>
              console.error('[EventEditScreen] Failed to reschedule local reminders:', err)
            );
          }
          await loadEvent(); // Reload the event to show updated data
        } else {
          const errorMessage = response.errors?.[0]?.message || t('calendar.eventEdit.updateFailed');
          showNotification('error', 'Error', errorMessage);
        }
      } else {
        // Create mode: create new event
        const finalCalendarId = selectedCalendarId || calendarId || formData.calendarId;
        if (!finalCalendarId) {
          showNotification('error', 'Error', t('calendar.eventEdit.selectCalendarError'));
          return;
        }

        const response = await apiService.createEvent({
          calendarId: finalCalendarId,
          businessId: currentBusiness._id,
          ...formData,
        });

        if (response.statusCode === 1000 && response.data) {
          showNotification('success', 'Success', t('calendar.eventEdit.createSuccess'));
          if (Array.isArray(formData.reminders) && formData.reminders.length > 0) {
            scheduleLocalReminders(response.data, formData.reminders).catch((err) =>
              console.error('[EventEditScreen] Failed to schedule local reminders:', err)
            );
          }
          const createdEventId = response.data._id || response.data.id;
          if (listId && createdEventId) {
            try {
              await apiService.linkDataListRecords(listId, [createdEventId]);
            } catch (err) {
              console.error('[EventEditScreen] Failed to link new event to data list:', err);
            }
            navigateTo('data-list-records', listId);
          } else {
            // Navigate to the newly created event's edit screen
            navigateTo('event-edit', createdEventId);
          }
        } else {
          const errorMessage = response.errors?.[0]?.message || t('calendar.eventEdit.createFailed');
          showNotification('error', 'Error', errorMessage);
        }
      }
    } catch (error: any) {
      console.error('Error saving event:', error);
      showNotification('error', 'Error', error.message || (eventId ? t('calendar.eventEdit.saveFailedUpdate') : t('calendar.eventEdit.saveFailedCreate')));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else {
      navigateTo('business-calendar');
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleJoinMeeting = () => {
    if (event?.onlineMeeting?.joinUrl) {
      Linking.openURL(event.onlineMeeting.joinUrl);
    }
  };

  const handleSave = () => {
    // Trigger form submission via ref
    if (formRef.current?.submit) {
      formRef.current.submit();
    }
  };

  const confirmDelete = async () => {
    if (!eventId || !currentBusiness?._id) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteEvent(eventId, currentBusiness._id);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.eventEdit.deleteSuccess'));
        cancelLocalReminders(eventId).catch((err) =>
          console.error('[EventEditScreen] Failed to cancel local reminders:', err)
        );
        if (event?.calendarId) {
          navigateTo('calendar-events', event.calendarId);
        } else {
          navigateTo('business-calendar');
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventEdit.deleteFailed');
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || t('calendar.eventEdit.deleteFailed'));
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const screenLayoutProps = {
    header: (
      <Header
        showNotifications={true}
        showMessages={true}
        onNotificationPress={() => navigateTo('notifications')}
        onMessagePress={() => navigateTo('chat')}
        showNavigation={Platform.OS === 'web'}
        activeTab={activeTab}
        onTabPress={handleTabPress}
      />
    ),
    showBottomTabs: Platform.OS !== 'web' as any,
    onTabPress: handleTabPress,
    showLeftPane,
    onLeftPaneClose: () => {},
    leftPaneContent: 'settings' as const,
    onSettingsItemPress: async (itemId: string) => {
      const { setPendingSettingsItem } = await import('../utils/tempStorage');
      await setPendingSettingsItem(itemId);
      navigateTo('settings');
    },
    showRightPane,
    onRightPaneClose: () => {},
    rightPaneContent: null as any,
  };

  if (loadingEvent) {
    return (
      <ScreenLayout {...screenLayoutProps}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>{t('calendar.eventEdit.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  // For create mode, event is null, which is fine
  // For edit mode, if event is null after loading, show error
  if (eventId && !event) {
    return (
      <ScreenLayout {...screenLayoutProps}>
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>{t('calendar.eventEdit.notFound')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  // For create mode, wait for calendars to load
  if (!eventId && loadingCalendars) {
    return (
      <ScreenLayout {...screenLayoutProps}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>{t('calendar.eventEdit.loadingCalendars')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  // For create mode, ensure we have at least one calendar
  if (!eventId && !loadingCalendars && calendars.length === 0) {
    return (
      <ScreenLayout {...screenLayoutProps}>
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>{t('calendar.eventEdit.noCalendarsAvailable')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  // Only calculate these if event exists (edit mode)
  const startDate = event ? (event.startDate instanceof Date ? event.startDate : new Date(event.startDate)) : new Date();
  const endDate = event ? (event.endDate instanceof Date ? event.endDate : new Date(event.endDate)) : new Date();
  const isSameDay = event ? startDate.toDateString() === endDate.toDateString() : false;
  const externalAttendees = event?.attendees?.filter((a: any) => a.type === 'external') || [];
  const userAttendees = event?.attendees?.filter((a: any) => a.type === 'user') || [];

  return (
    <ScreenLayout
      {...screenLayoutProps}
      formHeader={
        <FormHeader
          title={eventId ? t('calendar.eventEdit.editTitle') : t('calendar.eventEdit.createTitle')}
          subtitle={eventId ? t('calendar.eventEdit.updateSubtitle') : t('calendar.eventEdit.createSubtitle')}
          onBack={handleCancel}
          actions={[
            {
              icon: 'checkmark',
              onPress: handleSave,
              primary: true,
              loading,
              disabled: loading,
            },
            ...(event && (event.meetingType === 'online' || event.meetingType === 'hybrid') && event.onlineMeeting?.joinUrl
              ? [
                  {
                    icon: 'videocam',
                    onPress: handleJoinMeeting,
                    label: t('calendar.eventEdit.joinMeeting'),
                    accessibilityLabel: t('calendar.eventEdit.joinMeeting'),
                  } as FormHeaderAction,
                ]
              : []),
            ...(eventId
              ? [
                  {
                    icon: 'trash-outline',
                    onPress: () => setShowDeleteModal(true),
                    label: t('calendar.eventEdit.deleteConfirm'),
                    accessibilityLabel: 'Delete',
                  } as FormHeaderAction,
                ]
              : []),
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
        {/* Event Info Card - Only show in edit mode */}
        {event && (
          <View style={styles.card}>
            <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.iconContainer, { backgroundColor: (calendar?.color || colors.primary) + '15' }, rtl.marginEnd(spacing.md)]}>
                <Ionicons name="calendar-outline" size={24} color={calendar?.color || colors.primary} />
              </View>
              <View style={styles.cardHeaderContent}>
                <Text style={styles.cardTitle}>{event.title || t('calendar.eventEdit.untitledEvent')}</Text>
                {calendar && (
                  <View style={[styles.calendarTag, { backgroundColor: calendar.color + '20', flexDirection: rtl.rowDirection }]}>
                    <View style={[styles.calendarDot, { backgroundColor: calendar.color }]} />
                    <Text style={[styles.calendarTagText, { color: calendar.color }]}>{calendar.name}</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Date & Time Card - Only show in edit mode */}
        {event && (
          <View style={styles.card}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="time-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('calendar.eventEdit.dateTimeSection')}</Text>
            </View>
            <View style={styles.infoRow}>
              <View style={[styles.infoItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="calendar" size={16} color={colors.text.secondary} />
                <Text style={styles.infoValue}>{formatDate(startDate)}</Text>
              </View>
              {!event.isAllDay && (
                <View style={[styles.infoItem, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="time" size={16} color={colors.text.secondary} />
                  <Text style={styles.infoValue}>
                    {formatTime(startDate)} - {formatTime(endDate)}
                  </Text>
                </View>
              )}
              {event.isAllDay && (
                <View style={[styles.infoItem, { flexDirection: rtl.rowDirection }]}>
                  <Text style={styles.infoValue}>{t('calendar.eventEdit.allDay')}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Attendees Card - Contact Details - Only show in edit mode */}
        {event && (externalAttendees.length > 0 || userAttendees.length > 0) && (
          <View style={styles.card}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="people-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('calendar.eventEdit.attendeesSection')}</Text>
            </View>

            {/* External Attendees */}
            {externalAttendees.map((attendee: any, index: number) => (
              <View key={index} style={[styles.contactItem, { flexDirection: rtl.rowDirection }]}>
                <View style={[styles.contactAvatar, rtl.marginEnd(spacing.md)]}>
                  <Ionicons name="person" size={20} color={colors.primary} />
                </View>
                <View style={styles.contactInfo}>
                  <Text style={styles.contactName}>{attendee.external?.name || t('calendar.eventEdit.externalAttendeeFallback')}</Text>
                  {attendee.external?.email && (
                    <View style={[styles.contactDetail, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="mail-outline" size={14} color={colors.text.secondary} />
                      <Text style={styles.contactDetailText}>{attendee.external.email}</Text>
                      <TouchableOpacity onPress={() => Linking.openURL(`mailto:${attendee.external.email}`)}>
                        <Ionicons name="open-outline" size={14} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  )}
                  {attendee.external?.phone && (
                    <View style={[styles.contactDetail, { flexDirection: rtl.rowDirection }]}>
                      <Ionicons name="call-outline" size={14} color={colors.text.secondary} />
                      <Text style={styles.contactDetailText}>{attendee.external.phone}</Text>
                      <TouchableOpacity onPress={() => Linking.openURL(`tel:${attendee.external.phone}`)}>
                        <Ionicons name="call" size={14} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  )}
                  {attendee.status && <AttendeeStatusBadge status={attendee.status} />}
                </View>
              </View>
            ))}

            {/* User Attendees */}
            {userAttendees.map((attendee: any, index: number) => (
              <View key={`user-${index}`} style={[styles.contactItem, { flexDirection: rtl.rowDirection }]}>
                <View style={[styles.contactAvatar, rtl.marginEnd(spacing.md)]}>
                  <Ionicons name="person-circle-outline" size={24} color={colors.primary} />
                </View>
                <View style={styles.contactInfo}>
                  <Text style={styles.contactName}>{t('calendar.eventEdit.userIdLabel', { userId: attendee.userId })}</Text>
                  {attendee.status && <AttendeeStatusBadge status={attendee.status} />}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Location/Meeting Card - Only show in edit mode */}
        {event && (event.location || event.onlineMeeting) && (
          <View style={styles.card}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons
                name={event.meetingType === 'online' ? 'videocam-outline' : 'location-outline'}
                size={20}
                color={colors.primary}
              />
              <Text style={styles.sectionTitle}>
                {event.meetingType === 'online' ? t('calendar.eventEdit.onlineMeetingSection') : t('calendar.eventEdit.locationSection')}
              </Text>
            </View>
            {event.location?.name && (
              <View style={styles.infoRow}>
                <Ionicons name="location" size={16} color={colors.text.secondary} />
                <Text style={styles.infoValue}>{event.location.name}</Text>
              </View>
            )}
            {event.location?.address && (
              <View style={styles.infoRow}>
                <Text style={styles.infoSubtext}>{event.location.address}</Text>
              </View>
            )}
            {event.onlineMeeting?.joinUrl && (
              <TouchableOpacity
                style={[styles.joinButton, { flexDirection: rtl.rowDirection }]}
                onPress={() => Linking.openURL(event.onlineMeeting.joinUrl)}
              >
                <Ionicons name="videocam" size={18} color={colors.white} />
                <Text style={styles.joinButtonText}>{t('calendar.eventEdit.joinMeeting')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Event Form Card */}
        <View style={styles.card}>
          <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="create-outline" size={20} color={colors.primary} />
            <Text style={styles.sectionTitle}>{t('calendar.eventEdit.eventDetailsSection')}</Text>
          </View>
          <EventForm
            ref={formRef}
            initialData={event ? {
              ...event,
              // Ensure dates are Date objects
              startDate: event.startDate instanceof Date ? event.startDate : new Date(event.startDate),
              endDate: event.endDate instanceof Date ? event.endDate : new Date(event.endDate),
            } : selectedDate ? {
              // Pre-fill start date if selectedDate is provided (for new events) — EventForm
              // derives the end date from the calendar's slotDurationMinutes (falls back to 1h).
              startDate: selectedDate,
            } : undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={eventId ? t('calendar.eventEdit.updateSubmitLabel') : t('calendar.eventEdit.createSubmitLabel')}
            loading={loading}
            calendarId={selectedCalendarId || calendarId || event?.calendarId || ''}
            businessId={currentBusiness?._id || ''}
            eventId={eventId} // Pass eventId for attachment uploads
            calendars={calendars}
            onCalendarChange={setSelectedCalendarId}
            hideActions={true}
            slotDurationMinutes={calendar?.availability?.slotDurationMinutes}
          />
        </View>
      </ScrollView>
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('calendar.eventEdit.deleteTitle')}
        message={t('calendar.eventEdit.deleteMessage')}
        confirmText={t('calendar.eventEdit.deleteConfirm')}
        cancelText={t('calendar.eventEdit.deleteCancel')}
        onConfirm={confirmDelete}
        onClose={() => setShowDeleteModal(false)}
        loading={deleting}
        type="error"
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  contentContainer: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      },
      default: {
        elevation: 2,
      },
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: spacing.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardHeaderContent: {
    flex: 1,
  },
  cardTitle: {
    fontSize: typography.fontSize['2xl'],
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  calendarTag: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.sm,
    gap: spacing.xs / 2,
  },
  calendarDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  calendarTagText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  infoRow: {
    gap: spacing.sm,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  infoValue: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    flex: 1,
  },
  infoSubtext: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactInfo: {
    flex: 1,
    gap: spacing.xs,
  },
  contactName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  contactDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  contactDetailText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    flex: 1,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: spacing.borderRadius.md,
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  joinButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
});
